/**
 * Attaches the unified `instances` Proxy onto each Repeatable's compute-context
 * entry. At each level only DIRECT non-Repeatable children appear in the row
 * aggregate; nested Repeatables are wired by recursing into their resolved
 * instance entries, with the runtime block id chain joined by
 * RUNTIME_BLOCK_ID_SEPARATOR. Recursion is hard-capped at `maxDepth` (matches
 * the schema-gen depth cap). Per-row scope rewrites for in-row sibling access
 * live in `attachRowScopeIdentities`.
 *
 * `resetInstances` removes every materialized direct-child runtime instance for
 * the loaded slice; next read synthesizes from the parent template.
 */

import type { RepeatableBlockStateType } from '@unifyapps/defs/blocks/Repeatable/types';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { BlockControlMethod } from '../../types/blockDefinition';
import { getPrimaryKeyValue } from '../getPrimaryKeyValue';
import {
  getRuntimeBlockId,
  getRuntimeChildPrefix,
  RUNTIME_BLOCK_ID_SEPARATOR,
} from '../runTimeBlocks';
import { resolveBlockState } from './blockStateResolver';
import { createInstancesAccessor, type ResolvedInstance } from './instancesAccessor';

type BlocksState = Record<string, BlockStateUnionType | undefined>;

const DEFAULT_MAX_DEPTH = 50;

/**
 * Per-Repeatable runtime data + primary-key index. Keyed in the outer
 * `RuntimeSourceInfo.repeatables` record by the Repeatable's authoring blockId.
 */
export interface RepeatableInfo {
  data: readonly unknown[] | undefined;
  primaryKey: RepeatableBlockStateType['content']['primaryKey'];
  // Pre-computed by Repeatable.tsx; optional so legacy callers without published
  // fields still typecheck — accessor falls back to getPrimaryKeyValue / scans.
  itemPrimaryKeys?: readonly string[];
  itemKeyToIndex?: ReadonlyMap<string, number>;
}

/**
 * Per-build descriptor of every Repeatable on the page and which child blocks
 * live under each one. Built once per ContextBuilder build; powers the
 * `.instances` Proxy attachment and row-scope rewrites.
 *
 * - `childToRepeatable[childBlockId] === closestRepeatableAncestorBlockId`
 * - `repeatables[repeatableBlockId] === RepeatableInfo`
 *
 * Normalized split (vs. the prior per-child denormalized shape) so each parent
 * info is stored once, `canReuseBaseContext` can compare in O(unique-repeatables)
 * instead of O(total-children), and `filterRuntimeSourceInfoByCollectChildStates`
 * can iterate parents directly without a distinct-parents pre-pass.
 *
 * Keyspace note: at the top-level `runtimeSourceInfo`, `repeatables` keys are
 * AUTHORING blockIds (e.g. `b_repeat`). Inside the sub-map built by
 * `attachInstances` nested recursion, `repeatables` keys are RUNTIME instance
 * ids (e.g. `b_inner_<outerKey>`) — see the nested-recursion section below.
 * Consumers that walk repeatables must not assume homogeneous keyspace across
 * recursion levels.
 */
export interface RuntimeSourceInfo {
  childToRepeatable: Record<string, string | undefined>;
  repeatables: Record<string, RepeatableInfo | undefined>;
}

export interface RepeatableActions {
  removeBlocksState: (ids: string[]) => void;
  callBlockMethod: (params: {
    blockId: string;
    methodName: string;
    targetBlockId: string;
    methodPayload: unknown;
    instanceId?: string;
  }) => Promise<void>;
}

interface AttachInstancesArgs {
  blocksState: BlocksState;
  runtimeSourceInfo: RuntimeSourceInfo;
  instanceLookupState?: BlocksState;
  repeatableActions?: RepeatableActions;
  runtimeSourceBlockIds?: ReadonlySet<string>;
  // ContextMethodsState.blockMethods shape — runtime values are dispatcher objects
  // (createBlockMethodFunction), not BlockControlMethod[]. We only read keys here.
  blockMethods?: Record<string, BlockControlMethod[] | undefined>;
  maxDepth?: number;
  // Outer chain for nested-row instance keys (outer-most first); empty at the top level.
  parentInstanceKeyChain?: readonly string[];
  // Original (un-rewritten) runtime-source tree, threaded through every
  // recursion. Used by deeper levels to find descendants of the inner being
  // processed — without this, a level-N recursion only sees its own slice
  // and can't propagate `.instances` past depth 2. Defaults to
  // `runtimeSourceInfo` at the top-level call.
  fullRuntimeSourceInfo?: RuntimeSourceInfo;
}

const buildChainedKey = (chain: readonly string[], thisLevelKey: string): string =>
  chain.length === 0
    ? thisLevelKey
    : `${chain.join(RUNTIME_BLOCK_ID_SEPARATOR)}${RUNTIME_BLOCK_ID_SEPARATOR}${thisLevelKey}`;

const collectMaterializedIds = (childBlockIds: string[], lookupState: BlocksState): string[] => {
  const ids: string[] = [];
  // Snapshot lookup keys once; the prefix list (childBlockIds) is the small dim
  // and each key only needs one prefix match to qualify.
  const lookupKeys = Object.keys(lookupState);
  const prefixes = childBlockIds.map(getRuntimeChildPrefix);
  for (const key of lookupKeys) {
    for (const prefix of prefixes) {
      if (key.startsWith(prefix)) {
        ids.push(key);
        break;
      }
    }
  }
  return ids;
};

export function attachInstances(args: AttachInstancesArgs): BlocksState {
  const {
    blocksState,
    runtimeSourceInfo,
    instanceLookupState,
    repeatableActions,
    runtimeSourceBlockIds,
    blockMethods,
    maxDepth = DEFAULT_MAX_DEPTH,
    parentInstanceKeyChain = [],
  } = args;
  // Full tree defaults to the input on the top-level call; recursive callers
  // pass the same reference through so deeper levels can resolve descendants.
  const fullRuntimeSourceInfo = args.fullRuntimeSourceInfo ?? runtimeSourceInfo;

  const { childToRepeatable, repeatables } = runtimeSourceInfo;
  const childIds = Object.keys(childToRepeatable);
  if (childIds.length === 0) return blocksState;

  // Skip nested Repeatables in the flat row shape — they get their own accessor
  // via recursion below. Per-parent only ONE info entry is needed (data + primaryKey).
  const parentToChildren = new Map<string, string[]>();
  const parentToNestedRepeatableChildren = new Map<string, string[]>();
  const parentInfo = new Map<string, RepeatableInfo>();
  for (const childId of childIds) {
    const parentBlockId = childToRepeatable[childId];
    if (!parentBlockId) continue;
    const info = repeatables[parentBlockId];
    if (!info) continue;

    const isNestedRepeatable = runtimeSourceBlockIds?.has(childId) ?? false;
    const target = isNestedRepeatable ? parentToNestedRepeatableChildren : parentToChildren;
    const list = target.get(parentBlockId);
    if (list) list.push(childId);
    else target.set(parentBlockId, [childId]);
    if (!parentInfo.has(parentBlockId)) parentInfo.set(parentBlockId, info);
  }

  // Defense-in-depth: orphan childToRepeatable entries (parent key missing
  // from `repeatables`) leave parentInfo empty even when childIds.length > 0.
  if (parentInfo.size === 0) return blocksState;

  const result: BlocksState = { ...blocksState };
  const lookupState = instanceLookupState ?? blocksState;
  const getBlockConfig = (id: string) => result[id];

  // Fire-and-forget; the dispatcher in useComputeContextBuilder handles try/catch
  // + console.error internally. Promise<void> stays for the in-closure catch path.
  const callMethod = (params: {
    blockId: string;
    targetBlockId: string;
    methodName: string;
    methodPayload: unknown;
  }): void => {
    if (!repeatableActions) return;
    void repeatableActions.callBlockMethod({
      blockId: params.blockId,
      methodName: params.methodName,
      targetBlockId: params.targetBlockId,
      methodPayload: params.methodPayload,
    });
  };

  parentInfo.forEach((info, parentBlockId) => {
    // Skip nested parents at the top level — `parentBlockId` here is the
    // authoring id of a Repeatable that lives inside another Repeatable.
    // Its `.instances` accessor is written per-outer-row to its runtime
    // instance entry by the nested-iteration recursion below; writing an
    // empty (no parent data published on authoring) accessor here would
    // shadow that and contradict the "no global instances" rule.
    // At nested-recursion levels this skip never fires — the runtime id we
    // process there is synthetic and never appears in `fullRuntimeSourceInfo`.
    if (fullRuntimeSourceInfo.childToRepeatable[parentBlockId] !== undefined) return;
    const parentEntry = result[parentBlockId];
    if (!parentEntry) return;
    // Include nested-Repeatable children alongside regular children so the row
    // aggregate exposes them. Without this, `b_outer.instances[i].b_inner` is
    // undefined and the only nested-instances access is via the global last-
    // row-wins entry. The runtime instance for `b_inner` at this outer row
    // resolves below; the recursion further down attaches a row-scoped
    // `.instances` Proxy onto that runtime instance.
    const nestedChildBlockIds = parentToNestedRepeatableChildren.get(parentBlockId) ?? [];
    const directChildBlockIds = [
      ...(parentToChildren.get(parentBlockId) ?? []),
      ...nestedChildBlockIds,
    ];

    const resolveChildInstance = (
      childBlockId: string,
      primaryKeyValue: string,
    ): ResolvedInstance | undefined => {
      const chainedKey = buildChainedKey(parentInstanceKeyChain, primaryKeyValue);
      const runtimeId = getRuntimeBlockId({
        originalBlockId: childBlockId,
        runtimeBlockKey: chainedKey,
      });
      // Prefer the post-attach enriched entry from `result` so any `.instances`
      // accessor written by the nested recursion is visible to the row
      // aggregate. `lookupState` is the canonical raw instance store (used when
      // result hasn't been enriched yet, e.g., regular non-Repeatable children).
      const enriched = result[runtimeId];
      if (enriched) return enriched as ResolvedInstance;
      const resolved = resolveBlockState(runtimeId, lookupState, getBlockConfig);
      return resolved as ResolvedInstance | undefined;
    };

    const getMethodNamesForBlock = (childBlockId: string): readonly string[] => {
      // blockMethods is the registry-derived dispatcher dictionary
      // ({ methodId: dispatcherFn }); its keys are the only source of canonical
      // method names — BlockStateUnionType has no `methods` field at runtime.
      const methodFns = blockMethods?.[childBlockId];
      if (methodFns && typeof methodFns === 'object') {
        return Object.keys(methodFns);
      }
      return [];
    };

    const instances = createInstancesAccessor({
      parentData: info.data,
      parentPrimaryKey: info.primaryKey,
      itemPrimaryKeys: info.itemPrimaryKeys,
      itemKeyToIndex: info.itemKeyToIndex,
      childBlockIds: directChildBlockIds,
      resolveChildInstance,
      getMethodNamesForBlock,
      callMethod,
    });

    const enrichment: Record<string, unknown> = { instances };

    if (repeatableActions) {
      // Loaded-slice only; snapshot lookup-state keys at call time.
      // `directChildBlockIds` already includes nested-Repeatable children
      // (merged above), so no separate spread needed.
      enrichment.resetInstances = () => {
        const ids = collectMaterializedIds(directChildBlockIds, lookupState);
        if (ids.length > 0) repeatableActions.removeBlocksState(ids);
      };
    }

    result[parentBlockId] = {
      ...parentEntry,
      ...enrichment,
    } as BlockStateUnionType;
  });

  if (maxDepth <= 0) return result;
  if (parentToNestedRepeatableChildren.size === 0) return result;

  // Pre-group full-tree children by their (inner) Repeatable parent once, so the
  // per-(outer-row, inner-Repeatable) nested loop below is O(directChildren)
  // instead of re-scanning `fullRuntimeSourceInfo.childToRepeatable` per pair.
  // Shape: innerRepeatableAuthoringId -> [directChildIds]. Built off the FULL
  // tree (recursion-stable) — the same map is correct at every depth.
  const fullDirectChildrenByParent = new Map<string, string[]>();
  for (const fullChildId of Object.keys(fullRuntimeSourceInfo.childToRepeatable)) {
    const fullParentId = fullRuntimeSourceInfo.childToRepeatable[fullChildId];
    if (!fullParentId) continue;
    const existing = fullDirectChildrenByParent.get(fullParentId);
    if (existing) existing.push(fullChildId);
    else fullDirectChildrenByParent.set(fullParentId, [fullChildId]);
  }

  // Per-outer-row scoped inner.instances:
  // Each iteration writes the inner accessor to `result[innerRuntimeId]`
  // (the runtime instance state for THIS outer row's inner Repeatable), so
  // `b_outer.instances[i].b_inner.instances[j]` resolves to the inner row at
  // outer row i. The recursion's `subRuntimeSourceInfo.childToRepeatable[child]`
  // points to the runtime id so the recursive `attachInstances` writes its
  // accessor to that key.
  //
  // The global authoring entry `result[innerRepeatableId]` is intentionally
  // NOT given an `.instances` accessor — there is no canonical "the inner
  // Repeatable's instances" outside an outer row context. Bindings must go
  // through the outer accessor or row scope.
  parentToNestedRepeatableChildren.forEach((innerRepeatableChildIds, outerParentBlockId) => {
    const outerInfo = parentInfo.get(outerParentBlockId);
    if (!outerInfo) return;
    const outerData = outerInfo.data ?? [];
    for (let outerIndex = 0; outerIndex < outerData.length; outerIndex += 1) {
      const dataItem = outerData[outerIndex];
      if (dataItem === undefined) continue;
      // Prefer pre-computed itemPrimaryKeys; fall back for callers (or fixtures)
      // that haven't published the flat field yet.
      const outerKey =
        outerInfo.itemPrimaryKeys?.[outerIndex] ??
        String(
          getPrimaryKeyValue({ dataItem, index: outerIndex, primaryKey: outerInfo.primaryKey }),
        );

      for (const innerRepeatableId of innerRepeatableChildIds) {
        const innerRuntimeId = getRuntimeBlockId({
          originalBlockId: innerRepeatableId,
          runtimeBlockKey: buildChainedKey(parentInstanceKeyChain, outerKey),
        });
        const innerResolved = resolveBlockState(innerRuntimeId, lookupState, getBlockConfig) as
          | (BlockStateUnionType & {
              content?: {
                data?: unknown[];
                primaryKey?: RepeatableBlockStateType['content']['primaryKey'];
              };
              evaluatedData?: readonly unknown[];
              itemPrimaryKeys?: readonly string[];
              itemKeyToIndex?: ReadonlyMap<string, number>;
            })
          | undefined;
        if (!innerResolved) continue;
        // Prefer the inner Repeatable's published runtime fields; fall back to
        // content.data only when the inner hasn't published yet.
        const innerData = innerResolved.evaluatedData ?? innerResolved.content?.data;
        const innerPrimaryKey = innerResolved.content?.primaryKey;
        const innerItemPrimaryKeys = innerResolved.itemPrimaryKeys;
        const innerItemKeyToIndex = innerResolved.itemKeyToIndex;

        // sub-runtimeSourceInfo for the inner level: direct children of
        // `innerRepeatableId` from the FULL tree (not just the current
        // level's slice — that's what blocked propagation past depth 2).
        // The repeatable entry is keyed by `innerRuntimeId` so the recursive
        // `attachInstances` writes its accessor to the per-outer-row
        // runtime instance state.
        const directChildren = fullDirectChildrenByParent.get(innerRepeatableId);
        if (!directChildren || directChildren.length === 0) continue;

        const subChildToRepeatable: Record<string, string | undefined> = {};
        for (const innerChildId of directChildren) {
          subChildToRepeatable[innerChildId] = innerRuntimeId;
        }

        const subRuntimeSourceInfo: RuntimeSourceInfo = {
          childToRepeatable: subChildToRepeatable,
          repeatables: {
            [innerRuntimeId]: {
              data: innerData,
              primaryKey: innerPrimaryKey,
              itemPrimaryKeys: innerItemPrimaryKeys,
              itemKeyToIndex: innerItemKeyToIndex,
            },
          },
        };

        // Seed `result[innerRuntimeId]` with the resolved state so the
        // recursive call's `result[parentBlockId]` lookup succeeds even when
        // the inner Repeatable's runtime instance hasn't been materialized
        // into the global store yet (publish hook may publish on the
        // authoring entry; resolveBlockState falls back to authoring for
        // un-materialized runtime IDs).
        if (!result[innerRuntimeId]) {
          result[innerRuntimeId] = innerResolved as BlockStateUnionType;
        }

        const sub = attachInstances({
          blocksState: result,
          runtimeSourceInfo: subRuntimeSourceInfo,
          instanceLookupState: lookupState,
          repeatableActions,
          runtimeSourceBlockIds,
          blockMethods,
          maxDepth: maxDepth - 1,
          parentInstanceKeyChain: [...parentInstanceKeyChain, outerKey],
          fullRuntimeSourceInfo,
        });
        // Carry forward parent-entry rewrites from the recursive call. `sub` is
        // built by shallow-cloning `result` and then mutating specific keys
        // (inner/outer runtime entries), so every other key reference is
        // identical to `result`. A blanket assign is equivalent to the prior
        // per-key `!==` check and avoids the extra comparisons.
        Object.assign(result, sub);
      }
    }
  });

  return result;
}
